import React from "react";
import { ContextMenu, MenuItem } from "../common/Menu";

interface Props {
  icon?: string;
  label: string;
  description?: string;
  count?: number;
  // Shown as a trailing "›" to hint the row drills into another screen.
  drillsDown?: boolean;
  danger?: boolean;
  // Struck through and dimmed: present but switched off (a disabled rule).
  inactive?: boolean;
  /**
   * Commands for this row. Given a non-empty list the row grows a "⋯" button
   * that opens them — the touch stand-in for the desktop tree's right-click,
   * which has no equivalent here.
   */
  menuItems?: MenuItem[];
  onClick: () => void;
}

// A single tappable row used by every mobile drill-down list (the home screen
// of quadrants, folders, and the leaf tables). Big touch target, chevron on the
// right when it navigates deeper.
//
// The row is a flex container rather than one big button so the "⋯" can be a
// button of its own: nesting one inside the other would be invalid, and the two
// do different things.
const MobileListRow: React.FC<Props> = ({
  icon,
  label,
  description,
  count,
  drillsDown,
  danger,
  inactive,
  menuItems,
  onClick,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState<{ x: number; y: number } | null>(null);
  const hasMenu = !!menuItems && menuItems.length > 0;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        width: "100%",
        borderBottom: "1px solid #eee",
        background: "white",
        opacity: inactive ? 0.5 : 1,
      }}
    >
      <button
        onClick={onClick}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flex: 1,
          minWidth: 0,
          textAlign: "left",
          padding: "14px 16px",
          border: "none",
          background: "none",
          cursor: "pointer",
          font: "inherit",
        }}
      >
        {icon && (
          <span style={{ fontSize: "20px", width: "24px", textAlign: "center", flexShrink: 0 }}>
            {icon}
          </span>
        )}
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: "block",
              fontSize: "16px",
              color: danger ? "#d32f2f" : "#222",
              fontWeight: danger ? 600 : 500,
              textDecoration: inactive ? "line-through" : "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
          {description && (
            <span style={{ display: "block", fontSize: "13px", color: "#888", marginTop: "2px" }}>
              {description}
            </span>
          )}
        </span>
        {count !== undefined && (
          <span style={{ fontSize: "13px", color: "#aaa", flexShrink: 0 }}>{count}</span>
        )}
        {drillsDown && (
          <span style={{ fontSize: "20px", color: "#ccc", flexShrink: 0 }}>›</span>
        )}
      </button>

      {/* Always shown, unlike the desktop "⋯" — there's no hover to reveal it. */}
      {hasMenu && (
        <button
          type="button"
          aria-label={`Actions for ${label}`}
          onClick={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            setMenuAnchor({ x: rect.right, y: rect.bottom });
          }}
          style={{
            flexShrink: 0,
            padding: "0 16px",
            border: "none",
            borderLeft: "1px solid #f0f0f0",
            background: "none",
            color: "#888",
            font: "inherit",
            fontSize: "20px",
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ⋯
        </button>
      )}

      {menuAnchor && menuItems && (
        <ContextMenu
          anchor={menuAnchor}
          items={menuItems}
          touch
          onClose={() => setMenuAnchor(null)}
        />
      )}
    </div>
  );
};

export default MobileListRow;
